import { useEffect, useRef, useState } from "react";
import type { Date as PDate } from "@/generated/core/v1/date";
import { daysInMonth, MONTHS_FULL, MONTHS_SHORT, todayLocal } from "@/plan-logic/lib/dates";

/**
 * The prototype's year-first date picker (`dpNew`/`dpRender`): a pill that opens a 12-year grid,
 * then months (optional unless `day`), then days (only when `day`). `mode` limits it to the past
 * (birthdays) or the future (goal dates). Month 0 = year only; day 0 = month only (core.v1.Date).
 */
export function DatePicker({
  value,
  onChange,
  mode = "future",
  day = false,
  anchor,
  placeholder = "Pick a year",
  label,
  testId,
}: {
  value?: PDate;
  onChange: (d: PDate | undefined) => void;
  mode?: "future" | "past";
  day?: boolean;
  anchor?: number;
  placeholder?: string;
  label?: string;
  testId?: string;
}) {
  const now = todayLocal();
  const Y0 = now.year;
  const M0 = now.month - 1;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"y" | "m" | "d">("y");
  const [page, setPage] = useState(Y0);
  const [draft, setDraft] = useState<PDate | undefined>(value);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const set = !!value?.year;
  const text = !set
    ? placeholder
    : value!.day && value!.month
      ? `${value!.day} ${MONTHS_FULL[value!.month - 1]} ${value!.year}`
      : value!.month
        ? `${MONTHS_FULL[value!.month - 1]} ${value!.year}`
        : String(value!.year);

  const firstPage = () => {
    const y = value?.year;
    if (y) {
      if (mode === "past") return Math.min(Y0 - 11, y - ((((y - (anchor ?? Y0)) % 12) + 12) % 12));
      return Y0 + Math.floor((y - Y0) / 12) * 12;
    }
    return anchor ?? (mode === "past" ? Y0 - 45 : Y0);
  };

  const toggle = () => {
    if (open) return setOpen(false);
    setStep("y");
    setPage(firstPage());
    setOpen(true);
  };

  const pickYear = (y: number) => {
    let month = draft?.month ?? 0;
    if (
      month &&
      ((mode === "future" && y === Y0 && month - 1 < M0) ||
        (mode === "past" && y === Y0 && month - 1 > M0))
    )
      month = 0;
    const next = { year: y, month, day: day ? (draft?.day ?? 0) : 0 };
    setDraft(next);
    if (!day) onChange({ ...next, day: 0 });
    setStep("m");
  };
  const pickMonth = (m: number | null) => {
    const y = draft!.year;
    if (m === null || !day) {
      const next = { year: y, month: m === null ? 0 : m + 1, day: 0 };
      setDraft(next);
      onChange(next);
      setOpen(false);
      return;
    }
    setDraft({ year: y, month: m + 1, day: 0 });
    setStep("d");
  };
  const pickDay = (d: number) => {
    const next = { year: draft!.year, month: draft!.month, day: d };
    setDraft(next);
    onChange(next);
    setOpen(false);
  };

  return (
    <span className="yp" ref={ref} data-testid={testId}>
      <button
        type="button"
        className={`yp-pill${set ? " on" : ""}${open ? " open" : ""}`}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label ? `${label}: ${text}` : text}
      >
        <span>{text}</span>
        <span className="cv" />
      </button>
      {set && (
        <button
          type="button"
          className="yp-x"
          onClick={() => {
            onChange(undefined);
            setDraft(undefined);
            setOpen(false);
          }}
          aria-label="Clear this date"
        >
          &times;
        </button>
      )}
      {open && step === "y" && (
        <span className="yp-pop" role="dialog" aria-label="Pick a year">
          <span className="yp-head">
            <button
              type="button"
              className="yp-nav"
              disabled={mode === "future" && page <= Y0}
              onClick={() => setPage((p) => (mode === "future" ? Math.max(Y0, p - 12) : p - 12))}
              aria-label="Earlier years"
            >
              &#8249;
            </button>
            <span className="yp-range">
              {page} &ndash; {page + 11}
            </span>
            <button
              type="button"
              className="yp-nav"
              disabled={mode === "past" && page + 11 >= Y0}
              onClick={() => setPage((p) => (mode === "past" ? Math.min(Y0 - 11, p + 12) : p + 12))}
              aria-label="Later years"
            >
              &#8250;
            </button>
          </span>
          <span className="yp-grid">
            {Array.from({ length: 12 }, (_, k) => page + k).map((y) => (
              <button
                type="button"
                key={y}
                className={`yp-c${draft?.year === y ? " sel" : ""}`}
                disabled={(mode === "future" && y < Y0) || (mode === "past" && y > Y0)}
                onClick={() => pickYear(y)}
              >
                {y}
              </button>
            ))}
          </span>
        </span>
      )}
      {open && step === "m" && draft && (
        <span className="yp-pop" role="dialog" aria-label="Pick a month">
          <span className="yp-head">
            <button
              type="button"
              className="yp-nav"
              onClick={() => setStep("y")}
              aria-label="Back to years"
            >
              &#8249;
            </button>
            <span className="yp-range">{draft.year}</span>
            <span style={{ width: 26 }} />
          </span>
          <span className="yp-opt">{day ? "Which month?" : "A month is optional"}</span>
          <span className="yp-grid">
            {MONTHS_SHORT.map((mo, k) => (
              <button
                type="button"
                key={mo}
                className={`yp-c${draft.month === k + 1 ? " sel" : ""}`}
                disabled={
                  (mode === "future" && draft.year === Y0 && k < M0) ||
                  (mode === "past" && draft.year === Y0 && k > M0)
                }
                onClick={() => pickMonth(k)}
              >
                {mo}
              </button>
            ))}
          </span>
          {!day && (
            <button type="button" className="yp-skip" onClick={() => pickMonth(null)}>
              Just {draft.year} is fine
            </button>
          )}
        </span>
      )}
      {open && step === "d" && draft && draft.month > 0 && (
        <span className="yp-pop wide" role="dialog" aria-label="Pick a day">
          <span className="yp-head">
            <button
              type="button"
              className="yp-nav"
              onClick={() => setStep("m")}
              aria-label="Back to months"
            >
              &#8249;
            </button>
            <span className="yp-range">
              {MONTHS_FULL[draft.month - 1]} {draft.year}
            </span>
            <span style={{ width: 26 }} />
          </span>
          <span className="yp-opt">Which day?</span>
          <span className="yp-grid d7">
            {Array.from({ length: daysInMonth(draft.year, draft.month) }, (_, k) => k + 1).map(
              (d) => (
                <button
                  type="button"
                  key={d}
                  className={`yp-c${draft.day === d ? " sel" : ""}`}
                  disabled={
                    mode === "past" && draft.year === Y0 && draft.month - 1 === M0 && d > now.day
                  }
                  onClick={() => pickDay(d)}
                >
                  {d}
                </button>
              ),
            )}
          </span>
        </span>
      )}
    </span>
  );
}
